<template>
  <div class="tiles">
    <div v-for="(group, gIndex) in chunkedFeed" :key="gIndex" class="tile-group">
      <!-- Row 1: Full-width tile, 2 comments, two-line clamp -->
      <div v-if="group[0]" class="row row-1">
        <Entry
          :entry="group[0]"
          tileType="tile"
          :maxComments="2"
          class="tile tile-full"
        />
      </div>
      <!-- Row 2: Two side-by-side tiles, 1 comment each, one-line clamp -->
      <div v-if="group[1] || group[2]" class="row row-2 grid-two">
        <Entry
          v-if="group[1]"
          :entry="group[1]"
          tileType="tileOneLine"
          :maxComments="1"
          class="tile"
        />
        <Entry
          v-if="group[2]"
          :entry="group[2]"
          tileType="tileOneLine"
          :maxComments="1"
          class="tile"
        />
      </div>
      <!-- Row 3: Full-width tile, 3 comments, two-line clamp -->
      <div v-if="group[3]" class="row row-3">
        <Entry
          :entry="group[3]"
          tileType="tile"
          :maxComments="3"
          class="tile tile-full"
        />
      </div>
      <!-- Row 4: Two side-by-side tiles, 2 comments each, one-line clamp -->
      <div v-if="group[4] || group[5]" class="row row-4 grid-two">
        <Entry
          v-if="group[4]"
          :entry="group[4]"
          tileType="tileOneLine"
          :maxComments="2"
          class="tile"
        />
        <Entry
          v-if="group[5]"
          :entry="group[5]"
          tileType="tileOneLine"
          :maxComments="2"
          class="tile"
        />
      </div>
      <!-- Row 5: Two stacked entries on left (1 comment each) & one tall entry on right (3 comments) -->
      <div
        v-if="group[6] || group[7] || group[8]"
        class="row row-5 grid-two stack-mix"
      >
        <div class="stacked-two">
          <Entry
            v-if="group[6]"
            :entry="group[6]"
            tileType="tile"
            :maxComments="1"
            class="tile stacked"
          />
          <Entry
            v-if="group[7]"
            :entry="group[7]"
            tileType="tile"
            :maxComments="1"
            class="tile stacked"
          />
        </div>
        <Entry
          v-if="group[8]"
          :entry="group[8]"
          tileType="tile"
          :maxComments="3"
          class="tile tall"
        />
      </div>
      <!-- Row 6: Mirrored version of Row 5 -->
      <div
        v-if="group[9] || group[10] || group[11]"
        class="row row-6 grid-two stack-mix"
      >
        <Entry
          v-if="group[9]"
          :entry="group[9]"
          tileType="tile"
          :maxComments="3"
          class="tile tall"
        />
        <div class="stacked-two">
          <Entry
            v-if="group[10]"
            :entry="group[10]"
            tileType="tile"
            :maxComments="1"
            class="tile stacked"
          />
          <Entry
            v-if="group[11]"
            :entry="group[11]"
            tileType="tile"
            :maxComments="1"
            class="tile stacked"
          />
        </div>
      </div>
      <!-- Row 7: Two side-by-side tiles, two-line clamp, NO comments -->
      <div v-if="group[12] || group[13]" class="row row-7 grid-two small-font">
        <Entry
          v-if="group[12]"
          :entry="group[12]"
          tileType="tile"
          :maxComments="0"
          class="tile"
        />
        <Entry
          v-if="group[13]"
          :entry="group[13]"
          tileType="tile"
          :maxComments="0"
          class="tile"
        />
      </div>
      <!-- Row 8: If a 14th item exists, show it full-width on a new row -->
      <div v-if="group[14]" class="row row-8">
        <Entry
          :entry="group[14]"
          tileType="tile"
          :maxComments="2"
          class="tile tile-full"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Entry from "./Entry.vue";
export default {
  name: "Tiles",
  components: { Entry },
  props: {
    feed: {
      type: Array,
      required: true
    }
  },
  computed: {
    // Chunk the feed into groups of 14 items
    chunkedFeed() {
      const result = [];
      for (let i = 0; i < this.feed.length; i += 14) {
        result.push(this.feed.slice(i, i + 14));
      }
      return result;
    }
  }
};
</script>

<style scoped>
.tiles {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin: 2rem;
}
.tile-group {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}
.row {
  width: 100%;
  display: grid;
  gap: 2rem;
}
.row-1,
.row-3,
.row-8 {
  grid-template-columns: 1fr;
}
.grid-two {
  grid-template-columns: 1fr 1fr;
}
.stack-mix {
  align-items: stretch;
}
.stacked-two {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  height: 100%;
}
.stacked {
  flex: 1;
}
.tall {
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  flex: 1;
}
.small-font .heading {
  font-size: 0.85rem;
}
.small-font .title {
  font-size: 0.9rem;
}
.small-font .stats {
  font-size: 0.8rem;
}
.small-font .meta,
.small-font .source {
  font-size: 0.7rem;
}
/* Responsive adjustments */
@media (min-width: 1200px) {
  .tile-group {
    gap: 2rem;
  }
  .row {
    gap: 2rem;
  }
  .tiles {
    margin: 2rem;
  }
}
@media (max-width: 768px) {
  .tile-group {
    gap: 1rem;
  }
  .row {
    gap: 1rem;
  }
  .tiles {
    margin: 1rem;
  }
  /* For rows 2 & 4, stack to one column on mobile */
  .row.row-2,
  .row.row-4 {
    grid-template-columns: 1fr;
  }
  /* For row 7, maintain two columns */
  .row.row-7 {
    grid-template-columns: 1fr 1fr;
  }
  /* For stack-mix rows, stack vertically */
  .stack-mix {
    display: flex;
    flex-direction: column;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/site/Tiles.vue